import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from './Upload.module.scss';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { useApiContext } from '../../api';
import { modifyTemplate, selectTemplatesAndCombinedOntology } from '../../data/templateSlice';
import { NewDataUpload } from './elements/NewDataUpload';
import { DataUploadOverview } from './elements/DataUploadOverview';
import { UploadFile } from '../../components/files/helpers';
import { TpTemplateInfo, uploadTemplate } from '../../api/models/assets';
import TemplateEditor from '../../components/yaml-editor/TemplateEditor';
import schemaUploadTemplate from '../../json-schemas/schemaUploadTemplate';

export function Upload() {
  const { ontology, uploadTemplates, templates } = useAppSelector(selectTemplatesAndCombinedOntology);
  const api = useApiContext();
  const dispatch = useAppDispatch();
  const [selectedTemplate, setSelectedTemplate] = useState<uploadTemplate | null>(null);
  const [yamlTemplate, setYamlTemplate] = useState<TpTemplateInfo>();
  const [collectionProperties, setCollectionProperties] = useState<{ [id: string]: string }>({});
  const [filesSelected, setFilesSelected] = useState(false);

  const [fileSet, setFileSet] = useState<UploadFile[]>([]);

  const navigate = useNavigate();
  const { uploadTypeId } = useParams();

  const getTemplate = (uploadTypeId: string) => {
    const templateValues: uploadTemplate[] = Object.values(uploadTemplates);
    if (!uploadTypeId) return null;
    return templateValues.find((template: any) => template.id === uploadTypeId);
  };
  function handleCloseTemplateEditor(newContent: string) {
    if (!selectedTemplate || !yamlTemplate) return;
    // Persist the change to the server first, then dispatch the server response so the store
    // reflects the true, server-side `updatedAt` (required for correct recency ordering).
    api.template.modifyTemplate(yamlTemplate.id!, { yamlString: newContent }).then((updatedTemplate) => {
      dispatch(modifyTemplate({ template: updatedTemplate }));
    });
  }

  useEffect(() => {
    if (!uploadTypeId) return;
    const template = getTemplate(uploadTypeId);
    if (!template) return;
    setSelectedTemplate(template);
  }, [uploadTemplates, uploadTypeId]);

  useEffect(() => {
    if (!uploadTypeId) return;
    const template = getTemplate(uploadTypeId);
    if (!template) {
      setTimeout(() => {
        navigate('/');
      }, 50);
    } else {
      setSelectedTemplate(template);
      const allYamlTemplates = Object.values(templates.uploadTemplates).flat();
      const activeYamlTemplate = allYamlTemplates.find((t: any) => t.id === template.id);
      if (activeYamlTemplate) {
        setYamlTemplate(activeYamlTemplate);
      }
    }
  }, [uploadTypeId, templates.uploadTemplates]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.icon}>
        <FontAwesomeIcon icon={['fad', 'upload']} />
      </div>
      <div className={styles.title}>Data upload</div>
      <div className={styles.separator} />
      {selectedTemplate && (
        <div className={styles.UploadCard}>
          <div className={styles.uploadFlowHeader}>
            <div className={styles.uploadFlowTitle}>{selectedTemplate.displayName}</div>
          </div>
          <div className={styles.uploadFlowContent}>
            {filesSelected ? (
              <DataUploadOverview
                ontology={ontology}
                selectedTemplate={selectedTemplate}
                fileSet={fileSet}
                collectionProperties={collectionProperties}
                setFilesSelected={setFilesSelected}
              />
            ) : (
              <NewDataUpload
                ontology={ontology}
                selectedTemplate={selectedTemplate}
                fileSet={fileSet}
                setFileSet={setFileSet}
                collectionProperties={collectionProperties}
                setCollectionProperties={setCollectionProperties}
                setFilesSelected={setFilesSelected}
              />
            )}
          </div>
          {yamlTemplate && (
            <TemplateEditor
              initialTemplate={yamlTemplate.yamlString}
              contentModified={handleCloseTemplateEditor}
              yamlSchema={schemaUploadTemplate}
            />
          )}
        </div>
      )}
    </div>
  );
}
